import {AudioMergeMembers} from '@waka/compat/AudioMergeMembers';
import React from 'react';
import { useTranslations } from 'next-intl';
import { ChevronLeft, Select } from '@crabs-ai/web-icons';
import { SettingPanel } from '@/features/brand-template/ui/panels';
import { getGroupItemLabel } from '../items/group/group-label';
import type { GroupItem } from '../items/group/group-item-type';
import { enterGroupMember } from '../state/actions/select-group';
import {
  useAllItems,
  useSelectedItems,
  useWriteContext,
} from '../utils/use-context';
import { GroupMemberList } from './group-inspector';
import { AlignmentControls } from './controls/alignment-controls';
import { AnimationControls } from './controls/animation-controls';
import { resolveGroupGeometry } from '../items/group/group-geometry';
import { EMPTY_ANIMATIONS } from '../items/animation/animation-item-type';
import {fitGroupFrame} from '../items/group/fit-group-frame';

/**
 * 트랙에 배치된 1급 GroupItem(type:'group')을 통째로 선택했을 때의 inspector.
 *
 * - depth 미진입 → 멤버 목록(GroupMemberList). 멤버 클릭은 전역 selection·activeGroupId 를
 *   건드리지 않고 전역 inspectedGroupMemberId 만 set 한다(표시 전용, canvas 오버레이가 읽음).
 * - depth 진입 → "← 그룹으로" 백 버튼 + 그 멤버의 inspector. 멤버 inspector 는
 *   InspectorContent 재귀라 순환 import 를 피하려고 renderMemberInspector render prop 으로 받는다.
 *
 * depth state 는 전역 필드(inspectedGroupMemberId)라 다른 그룹의 멤버가 잔존할 수 있어
 * 이 그룹의 children 일 때만 depth 로 인정한다(A→B 전환 방어). 선택이 바뀌면 리듀서가 리셋.
 */
export const GroupItemInspector: React.FC<{
  group: GroupItem;
  renderMemberInspector: (itemIds: string[]) => React.ReactNode;
  collapseAllTrigger?: number;
  expandAllTrigger?: number;
}> = ({
  group,
  renderMemberInspector,
  collapseAllTrigger = 0,
  expandAllTrigger = 0,
}) => {
  const t = useTranslations();
  const { items } = useAllItems();
  const { inspectedGroupMemberId } = useSelectedItems();
  const { setState } = useWriteContext();

  // items 에 없는 children 은 방어적으로 제외한다.
  const memberIds = group.children.filter((id) => id in items);

  const activeMemberId =
    inspectedGroupMemberId != null && memberIds.includes(inspectedGroupMemberId)
      ? inspectedGroupMemberId
      : null;

  // 멤버 depth 진입/해제는 편집이 아니므로 undo 스택에 쌓지 않는다.
  const setActiveMember = (memberId: string | null) =>
    setState({
      update: (state) =>
        memberId == null ? state : enterGroupMember(state, group.id, memberId),
      commitToUndoStack: false,
    });

  if (activeMemberId != null) {
    return (
      <>
        <button
          type="button"
          onClick={() => setActiveMember(null)}
          className="flex w-full items-center gap-2xs border-b border-sys-style-neutral-light px-sm py-2xs text-body-small text-sys-cont-primary-default transition-colors hover:bg-sys-bg-surface-hover focus-visible:outline-none"
        >
          <ChevronLeft className="size-4" />
          {t('Editor.inspector.group.back-button')}
        </button>
        {renderMemberInspector([activeMemberId])}
      </>
    );
  }

  if(memberIds.length>0&&memberIds.every(id=>items[id]?.type==='audio'))return <AudioMergeMembers groupId={group.id} ids={memberIds}/>;

  // 멤버 텍스트 기반 대표 라벨이 있으면 그것을, 없으면 기존 그룹 타이틀을 쓴다.
  const label = getGroupItemLabel(group, items);

  // 그룹 애니(group.animations)는 컨테이너에 1회 적용된다(GroupItemLayer) — 카드 전체가 한
  // 블록으로 등장하는 그 애니다. 시간 기준은 children union(GroupItem 은 시간을 SoT 로 저장하지
  // 않는다) 이라 저장된 durationInFrames 대신 geometry 로 계산한다.
  const groupDurationInFrames = resolveGroupGeometry(group,
    items,
  ).durationInFrames;

  return (
    <>
      {/* 캔버스 정렬 — 그룹 여러 개 선택 시 useTargetItemIds 로 선택 전체에 적용된다. */}
      <div className="border-b border-sys-style-neutral-light px-md py-2xs">
        <AlignmentControls itemId={group.id} />
        {group.preserveGroupFrame ? <button type="button"
          disabled={!memberIds.length}
          title="그룹 여백과 효과 중심을 현재 내용에 맞춥니다. 실행 취소할 수 있습니다."
          onClick={() => setState({groupPropertyEdit:{type:'fit-frame',groupId:group.id},commitToUndoStack:true,update:state=>({...state,undoableState:{...state.undoableState,items:fitGroupFrame(state.undoableState.items,group.id)}})})}
          className="mt-2 rounded border px-3 py-1 text-sm disabled:opacity-50">
          내용에 그룹 맞추기
        </button> : null}
      </div>
      <div className="border-b border-sys-style-neutral-light">
        <AnimationControls
          itemId={group.id}
          animations={group.animations ?? EMPTY_ANIMATIONS}
          durationInFrames={groupDurationInFrames}
          collapseAllTrigger={collapseAllTrigger}
          expandAllTrigger={expandAllTrigger}
        />
      </div>
      <SettingPanel
        icon={<Select />}
        title={
          label ??
          t('Editor.inspector.group.title', { count: memberIds.length })
        }
      >
        <GroupMemberList
          memberIds={memberIds}
          onSelectMember={setActiveMember}
        />
      </SettingPanel>
    </>
  );
};
